/**
 * LLM Settings Component
 *
 * Allows users to configure their own LLM API key for generating
 * statistical analysis narratives.
 */

import React, { useState, useEffect } from 'react';
import { Key, Save, Trash2, AlertCircle, CheckCircle, Eye, EyeOff, Loader2 } from 'lucide-react';
import {
  storeLLMApiKey,
  getLLMApiKey,
  clearLLMApiKey,
  getMaskedApiKey,
  validateApiKey,
  getLLMProvider
} from '../utils/secureStorage';
import { testApiKey } from '../services/llmService';

interface LLMSettingsProps {
  onClose?: () => void;
  onApiKeySaved?: () => void;
}

export const LLMSettings: React.FC<LLMSettingsProps> = ({ onClose, onApiKeySaved }) => {
  const [provider, setProvider] = useState<'anthropic' | 'openai' | 'google'>('anthropic');
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [hasExistingKey, setHasExistingKey] = useState(false);

  useEffect(() => {
    const existingKey = getLLMApiKey();
    setHasExistingKey(!!existingKey);

    // Load stored provider if it exists
    const storedProvider = getLLMProvider();
    if (storedProvider) {
      setProvider(storedProvider);
    }
  }, []);

  const handleSave = async () => {
    // Validate
    const validation = validateApiKey(apiKey, provider);
    if (!validation.valid) {
      setTestResult({ success: false, message: validation.error || 'Ogiltig API-nyckel' });
      return;
    }

    // Test the key
    setTesting(true);
    setTestResult(null);

    const result = await testApiKey(apiKey, provider);

    if (result.success) {
      storeLLMApiKey(apiKey, provider);
      setTestResult({ success: true, message: 'API-nyckel sparad och verifierad!' });
      setHasExistingKey(true);
      setApiKey('');
      setShowKey(false);

      // Notify parent that API key was saved
      onApiKeySaved?.();

      // Auto-close after success
      setTimeout(() => {
        onClose?.();
      }, 2000);
    } else {
      const errorMsg = result.error || 'Okänt fel';

      // Check if it's a proxy connection error
      if (errorMsg.includes('proxy-servern')) {
        setTestResult({
          success: false,
          message: 'Proxy-servern är inte igång. Starta den med: npm run proxy'
        });
      } else {
        setTestResult({
          success: false,
          message: `API-nyckeln fungerar inte: ${errorMsg}`
        });
      }
    }

    setTesting(false);
  };

  const handleClear = () => {
    clearLLMApiKey();
    setHasExistingKey(false);
    setApiKey('');
    setTestResult({ success: true, message: 'API-nyckel borttagen' });
  };

  const maskedKey = getMaskedApiKey();

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-700 max-w-2xl shadow-2xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
          <Key className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">
            AI-Analys – API-nyckel
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Konfigurera din egen API-nyckel för AI-genererade sammanfattningar
          </p>
        </div>
      </div>

      {/* Existing Key Status */}
      {hasExistingKey && (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4 mb-6 border border-emerald-200 dark:border-emerald-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              <div>
                <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">
                  API-nyckel konfigurerad
                </p>
                <p className="text-xs text-emerald-700 dark:text-emerald-300 font-mono">
                  {maskedKey}
                </p>
              </div>
            </div>
            <button
              onClick={handleClear}
              className="px-3 py-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition-colors text-xs font-medium flex items-center gap-1"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Ta bort
            </button>
          </div>
        </div>
      )}

      {/* Provider Selection */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          AI-Modell
        </label>
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={() => setProvider('anthropic')}
            className={`p-4 rounded-lg border-2 transition-colors ${
              provider === 'anthropic'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
            }`}
          >
            <div className="text-sm font-semibold text-slate-900 dark:text-white">
              Claude Haiku 4.5
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Snabb & prisvärd
            </div>
          </button>
          <button
            onClick={() => setProvider('openai')}
            className={`p-4 rounded-lg border-2 transition-colors ${
              provider === 'openai'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
            }`}
          >
            <div className="text-sm font-semibold text-slate-900 dark:text-white">
              GPT-4o mini
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Balanserad
            </div>
          </button>
          <button
            onClick={() => setProvider('google')}
            className={`p-4 rounded-lg border-2 transition-colors ${
              provider === 'google'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
            }`}
          >
            <div className="text-sm font-semibold text-slate-900 dark:text-white">
              Gemini 2.0 Flash
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Experimentell
            </div>
          </button>
        </div>
      </div>

      {/* API Key Input */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          API-Nyckel
        </label>
        <div className="relative">
          <input
            type={showKey ? 'text' : 'password'}
            value={apiKey}
            onChange={(e) => {
              setApiKey(e.target.value);
              setTestResult(null);
            }}
            placeholder={
              provider === 'anthropic'
                ? 'sk-ant-api03-...'
                : provider === 'openai'
                ? 'sk-...'
                : 'AIza...'
            }
            className={`w-full px-4 py-2 pr-20 rounded-lg border ${
              apiKey && validateApiKey(apiKey, provider).valid
                ? 'border-emerald-300 dark:border-emerald-600 focus:ring-emerald-500'
                : 'border-slate-300 dark:border-slate-600 focus:ring-blue-500'
            } bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 font-mono text-sm transition-colors`}
          />
          {/* Validation indicator */}
          {apiKey && validateApiKey(apiKey, provider).valid && (
            <CheckCircle className="absolute right-12 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" />
          )}
          <button
            type="button"
            onClick={() => setShowKey(!showKey)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          >
            {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">
          {apiKey && validateApiKey(apiKey, provider).valid ? (
            <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
              ✓ Ser korrekt ut – klicka "Spara & Testa" för att verifiera
            </span>
          ) : (
            <>
              {provider === 'anthropic' ? (
                <>
                  Få din nyckel på{' '}
                  <a
                    href="https://console.anthropic.com/settings/keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    console.anthropic.com
                  </a>
                </>
              ) : provider === 'openai' ? (
                <>
                  Få din nyckel på{' '}
                  <a
                    href="https://platform.openai.com/api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    platform.openai.com
                  </a>
                </>
              ) : (
                <>
                  Få din nyckel på{' '}
                  <a
                    href="https://aistudio.google.com/apikey"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    aistudio.google.com
                  </a>
                </>
              )}
            </>
          )}
        </p>
      </div>

      {/* Test Result */}
      {testResult && (
        <div
          className={`rounded-lg p-3 mb-4 border ${
            testResult.success
              ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
              : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
          }`}
        >
          <div className="flex items-center gap-2">
            {testResult.success ? (
              <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
            )}
            <p
              className={`text-sm ${
                testResult.success
                  ? 'text-emerald-800 dark:text-emerald-200'
                  : 'text-red-800 dark:text-red-200'
              }`}
            >
              {testResult.message}
            </p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={handleSave}
          disabled={testing || !apiKey}
          className="flex-1 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white font-medium transition-colors disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {testing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Testar nyckel...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Spara & Testa
            </>
          )}
        </button>
        {onClose && (
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            Stäng
          </button>
        )}
      </div>

      {/* Collapsible explanation */}
      <details className="group">
        <summary className="cursor-pointer text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 flex items-center gap-2">
          <span className="group-open:rotate-90 transition-transform">▸</span>
          Varför behövs en API-nyckel?
        </summary>
        <ul className="mt-3 text-xs text-slate-600 dark:text-slate-400 space-y-1.5 pl-5">
          <li>• Din nyckel lagras <strong>endast i din webbläsare</strong> (localStorage)</li>
          <li>• Ingen nyckel skickas till våra servrar</li>
          <li>• Du betalar endast för det du använder direkt till AI-leverantören</li>
          <li>• Nyckeln kan raderas när som helst</li>
        </ul>
      </details>
    </div>
  );
};

export default LLMSettings;
